/**
 * Πλαϊνό πάνελ προϊόντος: Βασικά · Μεγέθη & τιμές · Επιλογές · Υλικά. Ό,τι αλλάζει μένει στο
 * πρόχειρο της σελίδας — αλλαγή προϊόντος ή καρτέλας δεν χάνει τίποτα.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Archive, Copy, FolderInput, MoreHorizontal, Trash2, X } from 'lucide-react';
import type { PanelTab } from './catalogChanges';
import type { ProductState } from './catalogDraft';
import type { ProductView } from './catalogView';
import ProductBasicsTab, { BasicsTabProps } from './ProductBasicsTab';
import ProductSizesTab, { SizesTabProps } from './ProductSizesTab';
import ProductOptionsTab, { OptionsTabProps } from './ProductOptionsTab';
import ProductIngredientsTab, { IngredientsTabProps } from './ProductIngredientsTab';
import { cx, MenuButton, ToneDot } from './ui';
import type { DragState } from './dragState';

export interface ProductPanelProps {
  view: ProductView;
  state: ProductState;
  isNew: boolean;
  crumb: { parent?: string; parentTone?: string | null; name: string };
  tab: PanelTab;
  onTab: (tab: PanelTab) => void;
  changedTabs: Set<PanelTab>;
  changedFields: Set<string>;
  readOnly: boolean;
  floating: boolean;
  showIngredients: boolean;
  drag: DragState;
  basics: Omit<BasicsTabProps, 'state' | 'changed' | 'readOnly' | 'isNew' | 'onChange' | 'onWithdraw' | 'onDiscard'>;
  sizes: Pick<SizesTabProps, 'feeGross' | 'sizeGroups' | 'onSizeVisibility' | 'onNewSizeGroup'>;
  options: Pick<OptionsTabProps, 'groups' | 'loaded' | 'categoryName' | 'othersInCategory' | 'onCreateGroup' | 'onOpenLibrary'>;
  ingredients: Pick<IngredientsTabProps, 'library' | 'newIngredients' | 'onCreateIngredient'>;
  onChange: (fn: (s: ProductState) => ProductState) => void;
  onClose: () => void;
  onDuplicate: () => void;
  onMove: () => void;
  onWithdraw: () => void;
  onDiscard: () => void;
}

const ProductPanel: React.FC<ProductPanelProps> = props => {
  const { t } = useTranslation(['catalog']);
  const { view, state, tab, readOnly, isNew } = props;
  const tabs: Array<{ id: PanelTab; label: string; count: number | null }> = [
    { id: 'basics', label: t('catalog:panel.basics'), count: null },
    { id: 'sizes', label: t('catalog:panel.sizes'), count: state.form.sizes.length },
    { id: 'options', label: t('catalog:panel.options'), count: state.groups ? state.groups.length : null },
    ...(props.showIngredients ? [{ id: 'ingredients' as PanelTab, label: t('catalog:panel.ingredients'), count: state.form.simple_ingredients.length }] : []),
  ];
  const active = tabs.some(x => x.id === tab) ? tab : 'basics';
  const name = state.form.name || t('catalog:product.untitled');

  const moreItems = [
    { key: 'dup', label: t('catalog:panel.duplicate'), icon: <Copy className="cx-ico" />, onSelect: props.onDuplicate, disabled: readOnly },
    { key: 'move', label: t('catalog:panel.move'), icon: <FolderInput className="cx-ico" />, onSelect: props.onMove, disabled: readOnly },
    'sep' as const,
    isNew
      ? { key: 'discard', label: t('catalog:product.discardNew'), icon: <Trash2 className="cx-ico" />, danger: true, onSelect: props.onDiscard }
      : { key: 'withdraw', label: t('catalog:withdraw.button'), icon: <Archive className="cx-ico" />, danger: true, onSelect: props.onWithdraw, disabled: readOnly },
  ];

  return (
    <aside className={cx('cx-pane cx-panel', props.floating && 'floating')} aria-label={t('catalog:panel.productAria', { name })}>
      <div className="cx-panel-head">
        <div className="cx-ph-text">
          <div className="cx-crumb">
            {props.crumb.parent && (<><ToneDot tone={props.crumb.parentTone} />{props.crumb.parent} ›</>)} {props.crumb.name}
          </div>
          <h2>
            {view.color && <span className="cx-ptone catalog-tone" data-tone={view.color} aria-hidden="true" />}
            <span className="cx-title-text">{name}</span>
            {isNew && <span className="cx-badge brand">{t('catalog:status.new')}</span>}
          </h2>
        </div>
        <MenuButton label={t('catalog:panel.more')} className="cx-icon-btn" align="right" items={moreItems}>
          <MoreHorizontal className="cx-ico" />
        </MenuButton>
        <button type="button" className="cx-icon-btn" onClick={props.onClose} aria-label={t('catalog:common.close')} title={t('catalog:common.close')}>
          <X className="cx-ico" />
        </button>
      </div>
      <div className="cx-tabs" role="tablist" aria-label={t('catalog:panel.tabsAria')}>
        {tabs.map(x => (
          <button
            key={x.id}
            type="button"
            role="tab"
            id={`cx-tab-${x.id}`}
            aria-selected={active === x.id}
            aria-controls="cx-tabpanel"
            className="cx-tab"
            onClick={() => props.onTab(x.id)}
          >
            {x.label}
            {x.count !== null && <span className="cx-n">{x.count}</span>}
            {props.changedTabs.has(x.id) && <span className="cx-edot" title={t('catalog:list.unsaved')} />}
          </button>
        ))}
      </div>
      <div className="cx-panel-body" id="cx-tabpanel" role="tabpanel" aria-labelledby={`cx-tab-${active}`}>
        {active === 'basics' && (
          <ProductBasicsTab
            key={view.key}
            {...props.basics}
            state={state}
            changed={props.changedFields}
            readOnly={readOnly}
            isNew={isNew}
            onChange={props.onChange}
            onWithdraw={props.onWithdraw}
            onDiscard={props.onDiscard}
          />
        )}
        {active === 'sizes' && (
          <ProductSizesTab
            key={view.key}
            {...props.sizes}
            productKey={view.key}
            state={state}
            changed={props.changedFields}
            readOnly={readOnly}
            drag={props.drag}
            onChange={props.onChange}
          />
        )}
        {active === 'options' && (
          <ProductOptionsTab
            key={view.key}
            {...props.options}
            productKey={view.key}
            state={state}
            changed={props.changedFields}
            readOnly={readOnly}
            drag={props.drag}
            onChange={props.onChange}
          />
        )}
        {active === 'ingredients' && props.showIngredients && (
          <ProductIngredientsTab
            key={view.key}
            {...props.ingredients}
            productKey={view.key}
            state={state}
            changed={props.changedFields}
            readOnly={readOnly}
            drag={props.drag}
            onChange={props.onChange}
          />
        )}
      </div>
    </aside>
  );
};

export default ProductPanel;
